/**
 * One saved item in the workspace: the whole card opens it, and everything
 * else it can do sits behind one menu so opening stays the obvious action.
 */
import { Clock3, History, MoreHorizontal, Pencil } from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { relativeTime } from "./workspaceView";

export interface CardAction {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

export interface CardVersion {
  id: string;
  label: string;
  updatedAt: string;
}

interface WorkspaceCardProps {
  title: string;
  icon: ReactNode;
  details: ReactNode;
  status: { className: string; label: string };
  updatedAt: string;
  preview?: ReactNode;
  footer?: ReactNode;
  layout: "grid" | "list";
  onOpen: () => void;
  actions: CardAction[];
  /** Earlier runs of the same request, newest first, excluding this one. */
  versions?: CardVersion[];
  /** What a re-run is called here. Documents say "runs": "v3" is a revision. */
  versionNoun?: string;
  onOpenVersion?: (id: string) => void;
  /** Present when the item can be renamed in place. */
  onRename?: (name: string) => Promise<boolean>;
}

/** Closes a popover on Escape or a click outside it, returning focus on Escape. */
function useDismiss(
  open: boolean,
  close: () => void,
  container: RefObject<HTMLElement | null>,
  trigger: RefObject<HTMLButtonElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) close();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      close();
      trigger.current?.focus();
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close, container, trigger]);
}

function CardMenu({ title, actions, onRename }: {
  title: string;
  actions: CardAction[];
  onRename?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useDismiss(open, () => setOpen(false), wrapper, trigger);

  useEffect(() => {
    if (open) wrapper.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
  }, [open]);

  const items = onRename
    ? [{ label: "Rename", icon: <Pencil size={14} />, onSelect: onRename }, ...actions]
    : actions;
  return (
    <div className="workspace-card-menu" ref={wrapper}>
      <button
        ref={trigger}
        type="button"
        className="icon-button"
        aria-label={`More actions for ${title}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div className="workspace-popover" role="menu" id={menuId}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionsButton({ versions, noun, onOpen }: {
  versions: CardVersion[];
  noun: string;
  onOpen: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useDismiss(open, () => setOpen(false), wrapper, trigger);
  const total = versions.length + 1;
  return (
    <div className="workspace-versions" ref={wrapper}>
      <button
        ref={trigger}
        type="button"
        className="version-badge"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <History size={12} /> {total} {noun}
      </button>
      {open && (
        <div className="workspace-popover versions" role="menu" aria-label={`Earlier ${noun}`}>
          <small>Earlier runs of this request</small>
          {versions.map((version) => (
            <button
              key={version.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpen(version.id);
              }}
              title={new Date(version.updatedAt).toLocaleString()}
            >
              <span>{version.label}</span>
              <small>{relativeTime(version.updatedAt)}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function WorkspaceCard({
  title,
  icon,
  details,
  status,
  updatedAt,
  preview,
  footer,
  layout,
  onOpen,
  actions,
  versions = [],
  versionNoun = "versions",
  onOpenVersion,
  onRename,
}: WorkspaceCardProps) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);

  async function submitRename(event: FormEvent) {
    event.preventDefault();
    const name = draft.trim();
    if (!onRename || !name || name === title) {
      setRenaming(false);
      return;
    }
    setSaving(true);
    const saved = await onRename(name);
    setSaving(false);
    if (saved) setRenaming(false);
  }

  return (
    <article className={`workspace-card ${layout}`}>
      {preview && layout === "grid" && <div className="workspace-card-preview">{preview}</div>}
      <div className="workspace-card-body">
        <div className="workspace-card-head">
          <span className="dashboard-icon">{icon}</span>
          <div className="workspace-card-heading">
            {renaming ? (
              <form className="workspace-rename" onSubmit={(event) => void submitRename(event)}>
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setRenaming(false);
                  }}
                  aria-label={`New name for ${title}`}
                  maxLength={200}
                  autoFocus
                  disabled={saving}
                />
                <button type="submit" className="button primary small" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" className="button tertiary small" onClick={() => setRenaming(false)}>
                  Cancel
                </button>
              </form>
            ) : (
              // The open control stretches over the card, so the whole card is
              // one target while the menu stays a separate control above it.
              <button type="button" className="workspace-card-open" onClick={onOpen} title={title}>
                {title}
              </button>
            )}
            <small>{details}</small>
          </div>
          <CardMenu
            title={title}
            actions={actions}
            onRename={onRename ? () => {
              setDraft(title);
              setRenaming(true);
            } : undefined}
          />
        </div>
        <div className="workspace-card-meta">
          <span className={`status-badge ${status.className}`}>{status.label}</span>
          {versions.length > 0 && onOpenVersion && (
            <VersionsButton versions={versions} noun={versionNoun} onOpen={onOpenVersion} />
          )}
          <span className="workspace-card-time" title={new Date(updatedAt).toLocaleString()}>
            <Clock3 size={12} /> {relativeTime(updatedAt)}
          </span>
        </div>
        {footer}
      </div>
    </article>
  );
}

export function WorkspaceCardSkeleton({ layout }: { layout: "grid" | "list" }) {
  return (
    <div className={`workspace-card skeleton ${layout}`} aria-hidden="true">
      {layout === "grid" && <div className="workspace-card-preview" />}
      <div className="workspace-card-body">
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}
